import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import SectionHeading from '../components/ui/SectionHeading';
import ConnectDotsBackground from '../components/ui/ConnectDotsBackground';

const MarTech = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-brand-dark transition-colors duration-300 relative overflow-hidden pt-20">
      <div
        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)',
          WebkitMaskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)'
        }}
      ></div>

      <ConnectDotsBackground />

      <div className="max-w-7xl mx-auto px-6 relative z-10 py-16 lg:py-24">
        {/* Header Section */}
        <div className="text-center mb-16">
          <SectionHeading title="The MarTech Toolbox" subtitle="Complete Stack Management" align="center" />
          <p className="text-lg text-slate-600 dark:text-slate-300 mb-8 max-w-2xl mx-auto font-medium">
            Take control of your marketing technology ecosystem. Diagnose the $80K Tool Trap™, evaluate your stack's health, and build a scalable system of record.
          </p>

          <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-2xl inline-block shadow-lg">
            <h3 className="text-3xl font-heading font-black text-brand-dark dark:text-white mb-2">$999</h3>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-6">One-Time Cost</span>
            <a
              href="https://www.paypal.com/ncp/payment/N5DPZRAAFMGXC"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 bg-brand-red text-white text-sm uppercase font-bold tracking-widest hover:bg-brand-yellow hover:text-brand-dark transition-all duration-300 rounded gap-2 shadow-lg shadow-brand-red/20"
            >
              <SafeIcon name="ShoppingCart" className="w-5 h-5" /> Get The Toolbox
            </a>
          </div>
        </div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Tool 1: Automated Pipeline Grader */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 rounded-2xl hover:border-brand-cyan transition-colors group"
          >
            <div className="w-14 h-14 bg-slate-50 dark:bg-brand-dark rounded-xl flex items-center justify-center mb-6 text-brand-dark dark:text-brand-cyan group-hover:bg-brand-cyan group-hover:text-brand-dark transition-colors">
              <SafeIcon name="Activity" className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-heading font-bold text-brand-dark dark:text-white mb-4">Automated Pipeline Grader</h3>
            <span className="text-[10px] uppercase tracking-widest text-brand-red dark:text-brand-yellow font-bold block mb-4">MarTech Stack Auditor</span>
            <p className="text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
              Instantly see redundancy, gaps, risk, and wasted spending. Our Jenga-inspired visualizer helps you pull out the tools you don't need without collapsing your entire marketing operation.
            </p>
          </motion.div>

          {/* Tool 2: Martech Stack Mapper */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="p-8 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 rounded-2xl hover:border-brand-cyan transition-colors group"
          >
            <div className="w-14 h-14 bg-slate-50 dark:bg-brand-dark rounded-xl flex items-center justify-center mb-6 text-brand-dark dark:text-brand-cyan group-hover:bg-brand-cyan group-hover:text-brand-dark transition-colors">
              <SafeIcon name="GitMerge" className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-heading font-bold text-brand-dark dark:text-white mb-4">MarTech Stack Mapper</h3>
            <span className="text-[10px] uppercase tracking-widest text-brand-red dark:text-brand-yellow font-bold block mb-4">AI Stack Builder Platform</span>
            <p className="text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
              Map your tools and run an AI evaluation on integration health, overall operational cost, and security compliance to ensure your data architecture is flawless.
            </p>
          </motion.div>

          {/* Tool 3: System of Record */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="p-8 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 rounded-2xl hover:border-brand-cyan transition-colors group"
          >
            <div className="w-14 h-14 bg-slate-50 dark:bg-brand-dark rounded-xl flex items-center justify-center mb-6 text-brand-dark dark:text-brand-cyan group-hover:bg-brand-cyan group-hover:text-brand-dark transition-colors">
              <SafeIcon name="Database" className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-heading font-bold text-brand-dark dark:text-white mb-4">System of Record</h3>
            <span className="text-[10px] uppercase tracking-widest text-brand-red dark:text-brand-yellow font-bold block mb-4">Centralized Documentation</span>
            <p className="text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
              Build a system to document, track, and manage your martech software and cloud stack in one definitive system of record (like Martechbase). Never lose track of renewals or licenses again.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default MarTech;